import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useMembers } from "@/context/MemberContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { VoiceMicButton } from "@/components/VoiceInput";
import { toast } from "sonner";
import { Field, Header, EmptyState } from "@/pages/Vitals";
import { Plus, Stethoscope, Trash2, MapPin, Calendar as CalIcon } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function Consultations() {
  const { activeId, active } = useMembers();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: today() });
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!activeId) return;
    api.get(`/consultations?member_id=${activeId}`).then(r => setItems(r.data));
  }, [activeId]);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.doctor_name) { toast.error("Enter doctor name"); return; }
    if (!activeId) { toast.error("Select a member first"); return; }
    setLoading(true);
    try {
      await api.post("/consultations", { ...form, member_id: activeId });
      toast.success("Consultation saved");
      setOpen(false); setForm({ date: today() }); load();
    } finally { setLoading(false); }
  };
  const del = async (id) => { await api.delete(`/consultations/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-6">
      <Header title="Doctor Consultations" subtitle="Record visits, diagnoses, and hospital details."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-consultation-btn" className="rounded-xl bg-[#4A6750] hover:bg-[#3B5340]"><Plus className="w-4 h-4 mr-2" /> Add Consultation</Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Add Consultation</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="consult-form">
                <Field label="Date"><Input data-testid="consult-date" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></Field>
                <Field label="Doctor Name"><div className="flex gap-2"><Input data-testid="consult-doctor" value={form.doctor_name || ""} onChange={e => setForm({...form, doctor_name: e.target.value})} required className="bg-[#F9F8F6] rounded-xl flex-1" /><VoiceMicButton onText={(t) => setForm(f => ({...f, doctor_name: f.doctor_name ? `${f.doctor_name} ${t}` : t}))} /></div></Field>
                <Field label="Specialization"><Input data-testid="consult-spec" placeholder="e.g. Cardiology" value={form.specialization || ""} onChange={e => setForm({...form, specialization: e.target.value})} /></Field>
                <Field label="Hospital / Clinic"><div className="flex gap-2"><Input data-testid="consult-hospital" value={form.hospital || ""} onChange={e => setForm({...form, hospital: e.target.value})} className="bg-[#F9F8F6] rounded-xl flex-1" /><VoiceMicButton onText={(t) => setForm(f => ({...f, hospital: f.hospital ? `${f.hospital} ${t}` : t}))} /></div></Field>
                <Field label="Diagnosis"><Input data-testid="consult-diagnosis" value={form.diagnosis || ""} onChange={e => setForm({...form, diagnosis: e.target.value})} /></Field>
                <Field label="Notes"><div className="flex gap-2"><Textarea data-testid="consult-notes" rows={3} value={form.notes || ""} onChange={e => setForm({...form, notes: e.target.value})} className="bg-[#F9F8F6] rounded-xl flex-1" /><VoiceMicButton onText={(t) => setForm(f => ({...f, notes: f.notes ? `${f.notes} ${t}` : t}))} /></div></Field>
                <Button type="submit" disabled={loading} data-testid="save-consult-btn" className="w-full rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">Save</Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      {items.length === 0 ? (
        <EmptyState icon={Stethoscope} title="No consultations yet" subtitle="Log your first doctor visit." />
      ) : (
        <div className="space-y-3">
          {items.map(c => (
            <Card key={c.id} className="p-5 rounded-2xl border border-[#E5E7E4]" data-testid={`consult-${c.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.1em] uppercase text-[#7D8F84]">
                    <CalIcon className="w-3 h-3" /> {c.date}
                  </div>
                  <h3 className="font-heading text-xl font-medium mt-1">Dr. {c.doctor_name}{c.specialization ? <span className="text-[#5C6D63] font-normal text-base"> · {c.specialization}</span> : null}</h3>
                  {c.hospital && <p className="flex items-center gap-1.5 text-sm text-[#5C6D63] mt-1.5"><MapPin className="w-3.5 h-3.5" /> {c.hospital}</p>}
                  {c.diagnosis && <p className="mt-2 text-sm"><span className="text-[#7D8F84] uppercase text-xs tracking-wider mr-2">Diagnosis</span><span className="text-[#1C2E24] font-medium">{c.diagnosis}</span></p>}
                  {c.notes && <p className="text-sm text-[#5C6D63] mt-2 italic">{c.notes}</p>}
                </div>
                <button data-testid={`del-consult-${c.id}`} onClick={() => del(c.id)} className="text-[#7D8F84] hover:text-[#C76B58] p-1.5"><Trash2 className="w-4 h-4" /></button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
